import {useMemo} from "react";

/**
 * Filters data points based on whether they fall within the given Google map boundaries.
 * @param data a list of data points to filter.
 * @param getLatLng a function that extracts the latitude and longitude from a data point.
 * This function is a dependency and may need to be memoized to prevent unnecessary recalculations.
 * @param mapBounds the current boundaries of the Google map.
 * @param shouldFilter a boolean indicating whether filtering should be applied.
 * @returns a filtered list of data points that are within the map boundaries.
 */
const useMapBoundaryFilter = (
  data: any[],
  getLatLng: (item: any) => (null | { lat: number; lng: number }),
  mapBounds: google.maps.LatLngBounds | undefined | null,
  shouldFilter: boolean
) => {

  return useMemo(() => {
    // if the NorthEastBound === the SouthWestBound then the mapBounds are a single point, return the list of data
    const ne = mapBounds?.getNorthEast();
    const sw = mapBounds?.getSouthWest();
    if (!shouldFilter || !mapBounds || !ne || !sw || ne.equals(sw) || data.length === 0) {
      return data;
    }
    return data.filter((item) => {
      const latLng = getLatLng(item);
      if (!latLng ||
        !Number.isFinite(latLng.lat) ||
        !Number.isFinite(latLng.lng)
      ) {
        return false;
      }
      return mapBounds.contains(latLng);
    });
  }, [data, getLatLng, mapBounds, shouldFilter]);
};

export default useMapBoundaryFilter;
